import {useTranslation, type WithTranslation} from "react-i18next";
import type {QuoteLineMO} from "~/OrdersQuotes/common/components/v2/models";
import {DateFormatType, formatNullableDate} from "~/utils/DateFormatter";
import {uniqueValues} from "~/utils/misc";
import {getTotalPrice} from "~/feature/ProductDescriptor/v1/productDescriptorUtils";
import {volatileSum} from "~/utils/PriceUtils";
import Chip from "~/common/components/Chip/Chip";
import {ReverseParam} from "~/common/components/Parameter";
import {Price} from "~/common/components/Price/v3/Price";

import classes from "./QuotePartsTable.module.less";
import type {NetPriceData, TotalPriceData, WithSurcharge} from "~/models/PriceData";
import type {RenderCboxFn} from "~/OrdersQuotes/common/components/v2/QuoteTable/types";
import type {ReactNode} from "react";
import {makeCountableParam} from "~/OrdersQuotes/common/components/v2/QuoteTable/useLineNoOffsetCalculation";
import {IncludedSurcharge} from "~/common/components/Product/ProductParams";
import {Stacker} from "~/common/components/Stacker/Stacker";
import {getEDD} from "~/OrdersQuotes/common/components/v2/QuoteTable/utils";

interface Props {
  quoteLine: QuoteLineMO;
  variant?: "main" | "alternative";
  forceRender?: boolean;
  hidden?: boolean;
  renderCheckbox?: RenderCboxFn;
}

export const QuotePartsTable = ({ quoteLine, variant = "main", forceRender, renderCheckbox, hidden }: Props) => {
  const { t } = useTranslation();
  if (!forceRender && variant === "main" && quoteLine.parts.length < 2) {
    return null;
  }

  const { header, parts } = sumParts({ quoteLine, variant, t });

  return (
    <table className={classes.table} {...makeCountableParam(quoteLine.lineNo)} hidden={hidden}>
      <thead>{renderRow({ row: header, t, Tag: "th", renderCheckbox: () => renderCheckbox?.(quoteLine) })}</thead>
      {parts.length > 0 && <tbody>{parts.map((part) => renderRow({ row: part, t, Tag: "td" }))}</tbody>}
    </table>
  );
};

function renderRow({
  row,
  t,
  Tag,
  renderCheckbox,
}: {
  row: SumPartsRow;
  t: WithTranslation["t"];
  Tag: "th" | "td";
  renderCheckbox?: () => ReactNode;
}) {
  return (
    <tr key={row.key}>
      <Tag>{row.title}</Tag>
      <Tag>
        <ReverseParam label={row.confirmedQuantity}>{row.requestedQuantity}</ReverseParam>
      </Tag>
      <Tag />
      <Tag>
        <ReverseParam label={row.estimatedDeliveryDate}>{row.requestedDeliveryDate}</ReverseParam>
      </Tag>
      <Tag />
      <Tag>
        {row.price && (
          <ReverseParam
            label={<Price price={row.price} zeroText={t("product.priceOnRequest")} surchargeEnabled={true} />}
          >
            <Price.NetPriceUnit price={row.price} variant={"short"} surchargeEnabled={true} />
          </ReverseParam>
        )}
      </Tag>
      <Tag>
        <Stacker gap={16}>
          <ReverseParam label={<Price as={"b"} price={row.totalPrice} surchargeEnabled={true} />}>
            <Price.TotalPriceUnit price={row.totalPrice} variant={"short"} surchargeEnabled={false} />
          </ReverseParam>
          <IncludedSurcharge calculateFrom={row.totalPrice} />
        </Stacker>
      </Tag>
      <Tag>{row.status}</Tag>
      <Tag>{renderCheckbox?.()}</Tag>
    </tr>
  );
}

interface SumPartsRow {
  key: string;
  title: JSX.Element;
  confirmedQuantity: number;
  requestedQuantity: number;
  estimatedDeliveryDate: string;
  requestedDeliveryDate: string;
  price?: NetPriceData;
  totalPrice: TotalPriceData & WithSurcharge;
  status?: JSX.Element;
}

function sumParts({
  quoteLine,
  variant,
  t,
}: Props & {
  t: WithTranslation["t"];
}) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const header: any = {
    key: "header",
  };
  if (variant === "main") {
    header.title = <b>Main quote line</b>;
  } else {
    header.title = <span>Alternative to quote line {quoteLine.alternativeTo}</span>;
  }
  header.confirmedQuantity = quoteLine.parts.reduce((acc, part) => acc + part.confirmedQuantity, 0);
  header.requestedQuantity = quoteLine.parts[0].requestedQuantity;
  const edds = uniqueValues(quoteLine.parts.map(getEDD));
  header.estimatedDeliveryDate = edds[0] + (edds.length > 1 ? ", ..." : "");
  header.requestedDeliveryDate = formatNullableDate(quoteLine.parts[0].requestedDeliveryDate, DateFormatType.DATE);

  const parts = quoteLine.parts.map((part) => {
    return {
      key: part.lineNo,
      title: `${quoteLine.lineNo}:${part.lineNo}`,
      confirmedQuantity: part.confirmedQuantity,
      requestedQuantity: part.requestedQuantity,
      estimatedDeliveryDate: getEDD(part),
      requestedDeliveryDate: formatNullableDate(part.requestedDeliveryDate, DateFormatType.DATE),
      price: part.price,
      totalPrice: getTotalPrice(part.price, part.confirmedQuantity),
    };
  });
  if (parts.length === 1) {
    header.price = parts[0].price;
    header.totalPrice = parts[0].totalPrice;
  } else {
    header.totalPrice = {
      ...volatileSum(...parts.map((part) => part.totalPrice)),
      isSubscription: parts[0].price?.isSubscription ?? false,
    };
  }
  header.status = <Chip>{t(`codes.quoteLineItemStatus.${quoteLine.status}`)}</Chip>;

  return {
    header,
    parts: parts.length > 1 ? parts : [],
  } as unknown as {
    header: SumPartsRow;
    parts: SumPartsRow[];
  };
}
